import { z } from "zod";
import { connectorIntegrationSchema, connectorProviderSchema, isUserScopedConnectorProvider, } from "./connectors.js";
import { runtimePluginSchema, runtimePluginSetupStatusSchema, } from "./runtime-plugins.js";
import { isSharedMailboxOwner } from "./shared-mailbox.js";
export const twinConnectionSourceSchema = z.enum([
    "business-data",
    "agent-capability",
]);
export const twinConnectionScopeSchema = z.enum(["organisation", "person", "mailbox"]);
export const twinConnectionAuthMethodSchema = z.enum([
    "oauth",
    "api-key",
    "service-account",
    "database",
    "automatic",
]);
export const twinConnectionStateSchema = z.enum([
    "not-connected",
    "action-required",
    "verifying",
    "ready",
    "error",
    "disabled",
]);
export const twinConnectionNextActionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("connect-in-browser"),
        label: z.string().trim().min(1),
        href: z.string().trim().min(1).optional(),
    }),
    z.object({
        kind: z.literal("provide-credentials"),
        label: z.string().trim().min(1),
        requiredFields: z.array(z.string().trim().min(1)),
    }),
    z.object({
        kind: z.literal("verify"),
        label: z.string().trim().min(1),
    }),
    z.object({
        kind: z.literal("wait"),
        label: z.string().trim().min(1),
    }),
    z.object({
        kind: z.literal("resolve-error"),
        label: z.string().trim().min(1),
    }),
    z.object({ kind: z.literal("none") }),
]);
export const twinConnectionSchema = z.object({
    id: z.string().trim().min(1),
    slug: z.string().trim().min(1),
    label: z.string().trim().min(1),
    description: z.string().trim().min(1),
    source: twinConnectionSourceSchema,
    scope: twinConnectionScopeSchema,
    authMethod: twinConnectionAuthMethodSchema,
    state: twinConnectionStateSchema,
    configured: z.boolean(),
    verified: z.boolean(),
    agentCanStart: z.boolean(),
    browserRequired: z.boolean(),
    detail: z.string().trim().min(1),
    nextAction: twinConnectionNextActionSchema,
    boundAccount: z.object({
        userId: z.string().trim().min(1).optional(),
        label: z.string().trim().min(1).optional(),
        email: z.string().trim().min(1).optional(),
        workspace: z.string().trim().min(1).optional(),
    }).optional(),
    lastVerifiedAt: z.number().int().nonnegative().optional(),
});
export const listTwinConnectionsQuerySchema = z.object({
    organisationId: z.string().trim().min(1).max(256),
});
export const listTwinConnectionsResponseSchema = z.object({
    organisationId: z.string().trim().min(1),
    connections: z.array(twinConnectionSchema),
});
export const verifyTwinConnectionRequestSchema = z.object({
    organisationId: z.string().trim().min(1).max(256),
    provider: connectorProviderSchema,
});
export const verifyTwinConnectionResponseSchema = z.object({
    connection: twinConnectionSchema,
    queuedJobCount: z.number().int().nonnegative(),
});
const CONNECTOR_PRESENTATION = {
    asana: ["Asana", "Selected project tasks and workspace metadata.", "oauth"],
    xero: ["Xero", "Accounting, invoices, payments, and financial records.", "oauth"],
    sun: ["SunSystems", "Finance and general-ledger records.", "service-account"],
    overture: ["Overture", "Operational data made available to your Twin.", "api-key"],
    stripe: [
        "Stripe",
        "Payments, billing, subscriptions, customers, disputes, refunds, and payouts.",
        "oauth",
    ],
    gocardless: [
        "GoCardless",
        "Open-banking accounts, balances, and booked transactions.",
        "api-key",
    ],
    sharepoint: ["SharePoint", "Documents and selected SharePoint sites.", "oauth"],
    gmail: ["Gmail", "Your work email and message history.", "oauth"],
    outlook_mail: ["Microsoft Outlook Mail", "Your Microsoft 365 work email and message history.", "oauth"],
    google_calendar: ["Google Calendar", "Your meetings and calendar events.", "oauth"],
    google_drive: ["Google Drive", "Your Drive files with native Docs and Sheets text extraction.", "oauth"],
    google_sheets: ["Google Sheets", "Rows from your spreadsheets, keyed by their header row.", "oauth"],
    github: ["GitHub", "Repositories, issues, and development activity.", "oauth"],
    granola: ["Granola", "Your meeting notes and transcripts.", "oauth"],
    slack: ["Slack", "Your messages, mentions, and conversations.", "oauth"],
    hubspot: [
        "HubSpot",
        "CRM contacts, companies, deals, pipelines, and engagement activity.",
        "oauth",
    ],
    twin: [
        "Twin Matching",
        "Confirmed links between records that describe the same thing in two connected systems.",
        "service-account",
    ],
};
/** A connector's name as people know it ("Xero", "SunSystems"), as the Connections page shows it. */
export function connectorDisplayName(provider) {
    return CONNECTOR_PRESENTATION[provider][0];
}
const PROVIDERS_LONGEST_FIRST = [...connectorProviderSchema.options].sort((left, right) => right.length - left.length);
/**
 * A warehouse relation or product slug as people read it, a leading connector by the name people know it by:
 * "xero_credit_note_lines" is "Xero credit note lines", "github-commit-register" is "GitHub commit register".
 */
export function relationInWords(name) {
    const text = name.replace(/[-_]+/gu, " ").trim();
    const provider = PROVIDERS_LONGEST_FIRST.find((candidate) => `${text} `.startsWith(`${candidate.replaceAll("_", " ")} `));
    if (provider)
        return `${connectorDisplayName(provider)}${text.slice(provider.length)}`;
    return text ? text[0].toUpperCase() + text.slice(1) : text;
}
const FIELD_ACRONYMS = new Set(["vat", "id", "url", "iban", "bic", "po"]);
/**
 * A field's key read as words, for anything without a declared label: "tax_type" reads "Tax type", "vat" reads "VAT".
 * The app's field lists and a Twin Build card's change line both use it, so a field reads the same in each.
 */
export function fieldLabel(key) {
    const words = key.split("_").filter(Boolean)
        .map((word) => (FIELD_ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word))
        .join(" ");
    return words ? words[0].toUpperCase() + words.slice(1) : key;
}
export const TWIN_CONNECTION_BROWSER_PATHS = {
    xero: "/api/connectors/xero/oauth/start",
    stripe: "/api/connectors/stripe/oauth/start",
    sharepoint: "/api/connectors/sharepoint/oauth/start",
    hubspot: "/api/connectors/hubspot/oauth/start",
    slack: "/api/electric-plugins/slack/oauth/start?capabilities=source_sync",
    microsoftTeams: "/api/electric-plugins/microsoft-teams/oauth/start",
    microsoftOutlook: "/api/electric-plugins/microsoft-outlook/oauth/start",
    googleWorkspace: "/api/electric-plugins/google-workspace/oauth/start",
};
const CONNECTOR_BROWSER_PATH_KEYS = {
    xero: "xero",
    stripe: "stripe",
    sharepoint: "sharepoint",
    gmail: "googleWorkspace",
    outlook_mail: "microsoftOutlook",
    google_calendar: "googleWorkspace",
    google_drive: "googleWorkspace",
    google_sheets: "googleWorkspace",
    slack: "slack",
    hubspot: "hubspot",
};
function connectorBrowserPath(provider) {
    const key = CONNECTOR_BROWSER_PATH_KEYS[provider];
    return key ? TWIN_CONNECTION_BROWSER_PATHS[key] : undefined;
}
export function unconfiguredConnectorTwinConnection(provider) {
    const parsedProvider = connectorProviderSchema.parse(provider);
    const [label, description, authMethod] = CONNECTOR_PRESENTATION[parsedProvider];
    const oauth = authMethod === "oauth";
    return twinConnectionSchema.parse({
        id: `connector:${parsedProvider}`,
        slug: parsedProvider,
        label,
        description,
        source: "business-data",
        scope: isUserScopedConnectorProvider(parsedProvider)
            ? "person"
            : "organisation",
        authMethod,
        state: "not-connected",
        configured: false,
        verified: false,
        agentCanStart: true,
        browserRequired: oauth,
        detail: `${label} is not connected.`,
        nextAction: oauth
            ? {
                kind: "connect-in-browser",
                label: `Connect ${label} in your browser`,
                href: connectorBrowserPath(parsedProvider),
            }
            : {
                kind: "provide-credentials",
                label: `Configure ${label}`,
                requiredFields: [],
            },
    });
}
/** One entry per connection: a provider can have organisation, personal and shared-mailbox ones. */
export function connectorTwinConnectionId(provider, integrationId) {
    return `connector:${provider}:${integrationId}`;
}
function configText(config, keys) {
    for (const key of keys) {
        const value = config[key];
        if (typeof value === "string" && value.trim())
            return value.trim();
    }
    return undefined;
}
/** The account a connection reads, so two connections for one provider can be told apart. */
function connectorBoundAccount(integration) {
    const account = {
        userId: integration.userId,
        email: configText(integration.config, ["googleAccountEmail", "microsoftUserEmail"]),
        label: configText(integration.config, ["googleAccountName", "microsoftUserName", "accountLogin"]),
        workspace: configText(integration.config, ["tenantName", "teamName"]),
    };
    return Object.values(account).some(Boolean) ? account : undefined;
}
export function connectorTwinConnection(integration) {
    const parsed = connectorIntegrationSchema.parse(integration);
    const [label, description, authMethod] = CONNECTOR_PRESENTATION[parsed.provider];
    const scope = isSharedMailboxOwner(parsed.userId)
        ? "mailbox"
        : parsed.userId !== undefined ? "person" : "organisation";
    const state = !parsed.enabled
        ? "disabled"
        : parsed.status === "error"
            ? "error"
            : parsed.status === "syncing"
                ? "verifying"
                : parsed.lastSuccessfulSyncAt !== undefined
                    ? "ready"
                    : "action-required";
    const nextAction = state === "error"
        ? { kind: "resolve-error", label: `Resolve the ${label} connection error` }
        : state === "verifying"
            ? { kind: "wait", label: `Wait for ${label} verification` }
            : state === "action-required"
                ? { kind: "verify", label: `Verify ${label}` }
                : { kind: "none" };
    return twinConnectionSchema.parse({
        id: connectorTwinConnectionId(parsed.provider, parsed.id),
        slug: parsed.provider,
        label,
        description,
        source: "business-data",
        scope,
        authMethod,
        state,
        configured: true,
        verified: state === "ready",
        agentCanStart: true,
        browserRequired: authMethod === "oauth",
        detail: state === "ready"
            ? `${label} is connected and verified.`
            : state === "verifying"
                ? `${label} verification is in progress.`
                : state === "error"
                    ? parsed.lastError ?? `${label} verification failed.`
                    : state === "disabled"
                        ? `${label} is disabled.`
                        : `${label} is configured but has not completed verification.`,
        nextAction,
        boundAccount: connectorBoundAccount(parsed),
        lastVerifiedAt: parsed.lastSuccessfulSyncAt,
    });
}
export function capabilityTwinConnection(plugin, setupStatus) {
    const parsedPlugin = runtimePluginSchema.parse(plugin);
    const parsedStatus = runtimePluginSetupStatusSchema.parse(setupStatus);
    if (parsedStatus.pluginSlug !== parsedPlugin.slug) {
        throw new Error("Plugin and setup status must have the same slug.");
    }
    const oauth = parsedStatus.requirement === "provider-managed-oauth" ||
        parsedStatus.requirement === "native-user-oauth" ||
        parsedStatus.requirement === "native-cli-login";
    const authMethod = parsedStatus.requirement === "organisation-data-source"
        ? "database"
        : parsedStatus.requirement === "plugin-secret"
            ? "api-key"
            : oauth
                ? "oauth"
                : "automatic";
    const state = parsedStatus.configured
        ? "ready"
        : parsedStatus.connectionStatus === "pending"
            ? "verifying"
            : parsedStatus.connectionStatus === "error"
                ? "error"
                : "not-connected";
    const nextAction = state === "ready"
        ? { kind: "none" }
        : state === "verifying"
            ? { kind: "wait", label: `Wait for ${parsedPlugin.label} connection` }
            : state === "error"
                ? {
                    kind: "resolve-error",
                    label: `Resolve the ${parsedPlugin.label} connection error`,
                }
                : oauth
                    ? {
                        kind: "connect-in-browser",
                        label: `Connect ${parsedPlugin.label} in your browser`,
                        href: capabilityBrowserPath(parsedPlugin),
                    }
                    : {
                        kind: "provide-credentials",
                        label: `Configure ${parsedPlugin.label}`,
                        requiredFields: parsedPlugin.secrets.map((secret) => secret.name),
                    };
    return twinConnectionSchema.parse({
        id: `capability:${parsedPlugin.slug}`,
        slug: parsedPlugin.slug,
        label: parsedPlugin.label,
        description: parsedPlugin.description,
        source: "agent-capability",
        scope: parsedStatus.requirement === "native-user-oauth" ||
            parsedStatus.requirement === "native-cli-login"
            ? "person"
            : "organisation",
        authMethod,
        state,
        configured: parsedStatus.configured,
        verified: parsedStatus.configured,
        agentCanStart: true,
        browserRequired: oauth,
        detail: parsedStatus.detail ??
            (parsedStatus.configured
                ? `${parsedPlugin.label} is ready.`
                : `${parsedPlugin.label} is not connected.`),
        nextAction,
        boundAccount: parsedStatus.accountLabel || parsedStatus.accountEmail || parsedStatus.workspaceName
            ? {
                label: parsedStatus.accountLabel,
                email: parsedStatus.accountEmail,
                workspace: parsedStatus.workspaceName,
            }
            : undefined,
    });
}
function capabilityBrowserPath(plugin) {
    if (plugin.integration?.provider === "composio") {
        return `/api/electric-plugins/composio/${encodeURIComponent(plugin.integration.toolkitSlug)}/connect/start`;
    }
    const nativePathKeys = {
        slack: "slack",
        "microsoft-teams": "microsoftTeams",
        "google-workspace": "googleWorkspace",
    };
    const key = nativePathKeys[plugin.slug];
    if (key)
        return TWIN_CONNECTION_BROWSER_PATHS[key];
    if (plugin.slug === "notion" && plugin.setup.auth === "native-cli-login") {
        return "/workspace/sessions?twin=plugins&twinPlugin=notion";
    }
    return undefined;
}
